import React, { useContext, createContext, useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faEdit } from "@fortawesome/free-solid-svg-icons";
import TableTotalNCost from "./TableTotalNCost";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import SearchFilter from "./SearchFilter";
import { NavigationContext } from "navigation-react";
import LoaderAnimation from "./LoaderAnimation";
import defaultimage from "./images/defaultimage.jpg";

// Helper function to safely parse dates
function parseDate(dateStr) {
  if (!dateStr) return null;
  const isoDate = new Date(dateStr);
  return isNaN(isoDate.getTime()) ? null : isoDate;
}

// Context for report details
export const ReportDetailsCont = createContext(null);
export const useReportDetails = () => useContext(ReportDetailsCont);

// Fetcher
const fetcher = (url) => fetch(url).then((res) => res.json());

// Main Component
const StaffRegister = () => {
  const { stateNavigator } = useContext(NavigationContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [requestStartDate, setRequestStartDate] = useState("");
  const [requestEndDate, setRequestEndDate] = useState("");

  // Base URL from environment
  const baseURL = process.env.REACT_APP_DRUPAL_BASE_URL;
  const staffApi = `${baseURL}/staff-register-api`;

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      mutate(staffApi);
    }, 5000);
    return () => clearInterval(interval);
  }, [staffApi]);

  const { data, error, isLoading } = useSWR(staffApi, fetcher);

  
  if (error)
    return (
      <LoaderAnimation loadingTxt="Failed to load staff. Please try again." />
    );
  if (isLoading) return <LoaderAnimation loadingTxt="Loading..." />;



  const filteredData = Array.isArray(data)
  ? data.filter((staff) => {
      // If no filters applied, show all
      const isFilterEmpty =
        !searchTerm.trim() && !requestStartDate && !requestEndDate;
      if (isFilterEmpty) return true;

      const search = searchTerm.trim().toLowerCase();

      // Exact matches
      const fullName = `${staff.field_firist_name || ""} ${staff.field_last_name || ""}`.toLowerCase();
      const matchesFullName = fullName === search;
      const matchesStaffID = (staff.field_staff_id || "").toLowerCase() === search;
      const matchesDepartment = (staff.field_department || "").toLowerCase() === search;
      const matchesEmail = (staff.field_email_address || staff.mail || "").toLowerCase() === search;
      const matchesPhone = (staff.field_phone_number || "").toLowerCase() === search;

      const matchesSearchTerm =
        matchesFullName || matchesStaffID || matchesDepartment || matchesEmail || matchesPhone;

      // Date filtering
      let matchesDate = true;
      if (requestStartDate || requestEndDate) {
        const staffDate = parseDate(staff.created);
        if (!staffDate) return false;

        const startDate = requestStartDate ? new Date(requestStartDate) : null;
        const endDate = requestEndDate ? new Date(requestEndDate) : null;

        if (startDate) startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(23, 59, 59, 999);

        matchesDate =
          (!startDate || staffDate >= startDate) && (!endDate || staffDate <= endDate);
      }

      return matchesSearchTerm && matchesDate;
    })
  : [];


  const totalStaff = filteredData.length;

  const iconStyle = (color) => ({
    backgroundColor: color,
    borderRadius: "50%",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus AddTitle="Staff Register" stateKey="staffaddregister" addLabel="Add New Staff" />

          {/* Stats Boxes */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              margin: "20px 0",
              gap: "20px",
            }}
          >
            {/* Total Staff */}
            <div
              style={{
                flex: 1,
                padding: "34px",
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                gap: "15px",
              }}
            >
              <div style={iconStyle("#007bff")}>
                <FontAwesomeIcon icon={faUsers} size="lg" color="#fff" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#333" }}>
                  Total Staff
                </h3>
                <p
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#007bff",
                  }}
                >
                  {totalStaff}
                </p>
              </div>
            </div>
          </div>

          {/* Search Filter */}
          <SearchFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            requestStartDate={requestStartDate}
            setRequestStartDate={setRequestStartDate}
            requestEndDate={requestEndDate}
            setRequestEndDate={setRequestEndDate}
          />

          {/* Staff Table */}
          {filteredData.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Picture</th>
                  <th>Full Name</th>
                  <th>Department</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((staff) => (
                  <tr key={staff?.uuid}>
                    <td>{staff?.field_staff_id || "-"}</td>
                    <td>
                      {staff?.field_staff_picture ? (
                        <img
                          src={`https://svms.salcommsystems.click${staff.field_staff_picture}`}
                          alt="staff"
                          width="40"
                          height="40"
                          style={{ borderRadius: "50%" }}
                        />
                      ) : (
                        <img src={defaultimage} alt="staff" width="40" height="40" style={{ borderRadius: "50%" }} />
                      )}
                    </td>
                    <td>{`${staff?.field_firist_name || ""} ${
                      staff?.field_last_name || ""
                    }`}</td>
                    <td>{staff?.field_department || "-"}</td>
                    <td>{staff?.field_email_address || staff?.mail || "-"}</td>
                    <td>{staff?.field_phone_number || "-"}</td>
                    <td>
                      <div
                        style={{
                          background:
                            staff?.status_1 === "Inactive" ? "red" : "#05A550",
                          textAlign: "center",
                          color: "#fff",
                          borderRadius: 50,
                          padding: "3px 8px",
                        }}
                      >
                        {staff?.status_1}
                      </div>
                    </td>
                    <td>{staff?.created}</td>
                    <td className="formActionBtn">
                      <button
                        className="actionBTN"
                        onClick={() => {
                          stateNavigator.navigate("staffeditregister", {
                            staffUUID: staff?.uuid,
                            staffID: staff?.field_staff_id,
                            firstName: staff?.field_firist_name,
                            lastName: staff?.field_last_name,
                            department: staff?.field_department,
                            phone: staff?.field_phone_number,
                            email: staff?.field_email_address,
                            departmemtUUID: staff?.uuid_1,
                            statusIDTxT: staff?.status_1,
                            statusID: staff?.status,
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faEdit} size={16} color="gray" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <LoaderAnimation loadingTxt="No matching staff found" />
          )}

          {/* Summary */}
          {filteredData.length > 0 && (
            <TableTotalNCost
              noOfReportLable="Number of Staff :"
              noOfReportLength={filteredData.length}
              totalReportFeeCostLable=""
              totalReportFeeCostValue={null}
            />
          )}
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
};

export default StaffRegister;
